import { useState } from 'react'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { apiFetch } from '../api/client'
import { useForbiddenGuard } from '../api/guard'
import { isUuid, shortId } from './ids'
import './screens.css'

type SessionStatus = 'active' | 'expired' | 'revoked'

// The backend never returns token_hash, token_prefix or metadata, and this view shows
// no token fields of any kind.
interface ToolSession {
  id: string
  workspace_id: string
  end_user_id: string | null
  created_by_user_id: string | null
  created_by_api_key_id: string | null
  toolkits: string[] | null
  tools: string[] | null
  expires_at: string
  revoked_at: string | null
  revoked_reason: string | null
  last_used_at: string | null
  created_at: string | null
  status: SessionStatus
}
interface ToolSessionsResponse {
  tool_sessions: ToolSession[]
  limit: number
  offset: number
  has_more: boolean
}

const PAGE_SIZE = 50

const BADGE: Record<SessionStatus, string> = {
  active: 'scr-badge scr-badge--success',
  expired: 'scr-badge scr-badge--denied',
  revoked: 'scr-badge scr-badge--error',
}

function when(v: string | null): string {
  return v ? new Date(v).toLocaleString() : '—'
}

export function ToolSessionsView() {
  const [workspace, setWorkspace] = useState('')
  const [status, setStatus] = useState<'' | SessionStatus>('')
  const [offset, setOffset] = useState(0)

  const ws = workspace.trim()
  const wsInvalid = ws !== '' && !isUuid(ws)
  const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) })
  if (ws && !wsInvalid) params.set('workspace_id', ws)
  if (status) params.set('status', status)

  const query = useQuery<ToolSessionsResponse>({
    queryKey: ['admin-tool-sessions', wsInvalid ? '' : ws, status, offset],
    queryFn: () => apiFetch<ToolSessionsResponse>(`/admin/tool-sessions?${params.toString()}`),
    placeholderData: keepPreviousData,
  })
  useForbiddenGuard(query.error)

  const rows = query.data?.tool_sessions ?? []
  const hasMore = query.data?.has_more ?? false

  return (
    <section className="scr" aria-labelledby="ts-heading">
      <div className="screen-head">
        <h2 id="ts-heading">Tool Sessions</h2>
      </div>

      <div className="scr-filters">
        <input
          type="text"
          aria-label="Filter by workspace ID"
          placeholder="Workspace ID (UUID)"
          value={workspace}
          onChange={(e) => {
            setWorkspace(e.target.value)
            setOffset(0)
          }}
        />
        <select
          aria-label="Filter by status"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as '' | SessionStatus)
            setOffset(0)
          }}
        >
          <option value="">All statuses</option>
          <option value="active">active</option>
          <option value="expired">expired</option>
          <option value="revoked">revoked</option>
        </select>
      </div>
      {wsInvalid && <p className="scr-err">Workspace ID must be a UUID; filter not applied.</p>}

      <p className="scr-note">Read-only, across all workspaces, newest first.</p>

      {query.isError && (query.error as { status?: number }).status !== 403 && (
        <p className="screen-error" role="alert">
          Failed to load tool sessions.
        </p>
      )}

      <table className="scr-table">
        <thead>
          <tr>
            <th>Session</th>
            <th>Workspace</th>
            <th>End user</th>
            <th>Toolkits</th>
            <th>Status</th>
            <th>Expires</th>
            <th>Last used</th>
            <th>Created</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => (
            <tr key={s.id} className={s.status === 'active' ? undefined : 'scr-row--muted'}>
              <td className="scr-mono" title={s.id}>{shortId(s.id)}</td>
              <td className="scr-mono" title={s.workspace_id}>{shortId(s.workspace_id)}</td>
              <td className="scr-mono" title={s.end_user_id ?? ''}>
                {s.end_user_id ? shortId(s.end_user_id) : '—'}
              </td>
              <td>
                {!s.toolkits || s.toolkits.length === 0 ? (
                  <span className="scr-muted">—</span>
                ) : (
                  <span className="scr-chips">
                    {s.toolkits.map((t) => (
                      <span key={t} className="scr-chip">
                        {t}
                      </span>
                    ))}
                  </span>
                )}
              </td>
              <td>
                <span className={BADGE[s.status] ?? 'scr-badge'}>{s.status}</span>
                {s.status === 'revoked' && s.revoked_reason && (
                  <div className="scr-muted">{s.revoked_reason}</div>
                )}
              </td>
              <td>{when(s.expires_at)}</td>
              <td>{when(s.last_used_at)}</td>
              <td>{when(s.created_at)}</td>
            </tr>
          ))}
          {!query.isLoading && rows.length === 0 && (
            <tr>
              <td colSpan={8} className="ws-empty">
                No tool sessions.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="ws-pager">
        <button
          type="button"
          disabled={offset === 0}
          onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
        >
          Previous
        </button>
        <span className="ws-count">Page {Math.floor(offset / PAGE_SIZE) + 1}</span>
        <button type="button" disabled={!hasMore} onClick={() => setOffset((o) => o + PAGE_SIZE)}>
          Next
        </button>
      </div>
    </section>
  )
}
